// src/components/wizard/WizardFlow.jsx
// ─────────────────────────────────────────────────────────────
// Covers: WZ-01 WZ-02 PL-04
// Renders the correct step based on wizard.step
// ─────────────────────────────────────────────────────────────

import React, { useEffect, useRef } from 'react';
import { WIZARD_STEPS } from '../../hooks/useWizard';
import WizardWelcome    from './WizardWelcome';
import WizardWho        from './WizardWho';
import WizardChallenge  from './WizardChallenge';
import WizardProvince   from './WizardProvince';
import WizardRefinement from './WizardRefinement';
import styles from './WizardFlow.module.css';

const PROVINCE_LABELS = {
  BC:'BC', AB:'AB', SK:'SK', MB:'MB', ON:'ON', QC:'QC',
  NB:'NB', NS:'NS', PE:'PEI', NL:'NL', YT:'YT', NT:'NT', NU:'NU',
};

export default function WizardFlow({ wizard }) {
  const {
    step, answers, progress,
    goToStep, goBack, toggleMulti,
    setProvince, setRefinement,
    completeWizard, skipWizard,
  } = wizard;

  // Scroll to top on step change
  const topRef = useRef(null);
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step]);

  // Steps 1–3 need a selection before continuing
  const canContinue = {
    [WIZARD_STEPS.WHO]:        answers.who.length > 0,
    [WIZARD_STEPS.CHALLENGE]:  answers.challenge.length > 0,
    [WIZARD_STEPS.PROVINCE]:   answers.province !== null,
  };

  return (
    <div className={styles.shell} ref={topRef}>

      {/* Header */}
      <header className={styles.header}>
        <a href="/" className={styles.logo}>
          <span className={styles.logoMark}>◉</span>
          <span className={styles.logoName}>
            Signal<span className={styles.logoAccent}>Post</span>
          </span>
        </a>
        {step !== WIZARD_STEPS.WELCOME && (
          <button className={styles.skipBtn} onClick={skipWizard}>
            Browse all programs →
          </button>
        )}
      </header>

      {/* Progress bar — shown on steps 1–4 */}
      {step > WIZARD_STEPS.WELCOME && (
        <div className={styles.progressWrap}>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${progress}%` }} />
          </div>
          <span className={styles.progressLabel}>Step {step} of 4</span>
        </div>
      )}

      {/* Step content */}
      <main className={styles.stage}>
        {step === WIZARD_STEPS.WELCOME && (
          <WizardWelcome
            onStart={() => goToStep(WIZARD_STEPS.WHO)}
            onSkip={skipWizard}
          />
        )}
        {step === WIZARD_STEPS.WHO && (
          <WizardWho
            selected={answers.who}
            onToggle={(val) => toggleMulti('who', val)}
            canContinue={canContinue[WIZARD_STEPS.WHO]}
            onBack={() => goToStep(WIZARD_STEPS.WELCOME)}
            onNext={() => goToStep(WIZARD_STEPS.CHALLENGE)}
            onSkip={skipWizard}
          />
        )}
        {step === WIZARD_STEPS.CHALLENGE && (
          <WizardChallenge
            selected={answers.challenge}
            onToggle={(val) => toggleMulti('challenge', val)}
            canContinue={canContinue[WIZARD_STEPS.CHALLENGE]}
            onBack={() => goToStep(WIZARD_STEPS.WHO)}
            onNext={() => goToStep(WIZARD_STEPS.PROVINCE)}
            onSkip={skipWizard}
          />
        )}
        {step === WIZARD_STEPS.PROVINCE && (
          <WizardProvince
            selected={answers.province}
            onSelect={setProvince}
            canContinue={canContinue[WIZARD_STEPS.PROVINCE]}
            onBack={() => goToStep(WIZARD_STEPS.CHALLENGE)}
            onNext={() => goToStep(WIZARD_STEPS.REFINEMENT)}
            onSkip={skipWizard}
            labels={PROVINCE_LABELS}
          />
        )}
        {step === WIZARD_STEPS.REFINEMENT && (
          <WizardRefinement
            selected={answers.refinement}
            onSelect={(val) => {
              setRefinement(val);
              completeWizard({ ...answers, refinement: val });
            }}
            onBack={() => goToStep(WIZARD_STEPS.PROVINCE)}
            onSkip={skipWizard}
          />
        )}
      </main>

      {/* Crisis strip — always visible */}
      <div className={styles.crisisStrip}>
        <span className={styles.crisisLabel}>Need immediate support?</span>
        <a href="tel:18002687708" className={styles.crisisLink}>
          VAC / CAF — 1-800-268-7708
        </a>
        <a href="tel:18334564566" className={styles.crisisLink}>
          Talk Suicide — 1-833-456-4566
        </a>
      </div>
    </div>
  );
}
